import { SectionTitle } from "@/components/SectionHeader";
import { Eyebrow } from "@/components/Eyebrow";
import { ButtonLink } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import type { ResponsiveImage } from "@/lib/responsiveImage";

type Props = {
  id: string;
  image: ResponsiveImage;
  imageAlt: string;
  imageSide: "left" | "right";
  eyebrow?: string;
  title?: string;
  body: string;
  cta: { label: string; href: string };
  cropImageOnMobile?: boolean;
};

export function SplitSection({
  id,
  image,
  imageAlt,
  imageSide,
  eyebrow,
  title,
  body,
  cta,
  cropImageOnMobile,
}: Props) {
  return (
    <section
      id={id}
      className="px-4 py-16 md:px-6 lg:py-16 [&+&]:pt-4 md:[&+&]:pt-16"
    >
      <div
        className={cn(
          "mx-auto flex max-w-6xl flex-col-reverse gap-12 md:grid md:grid-cols-2 md:items-center md:gap-10 lg:gap-16",
        )}
      >
        <div
          className={cn(
            "flex max-w-md flex-col gap-12 px-4 md:gap-16 md:px-0",
            imageSide === "left" &&
              "md:col-start-2 md:row-start-1 md:justify-self-end",
          )}
        >
          <div className="flex flex-col gap-6">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {title && <SectionTitle>{title}</SectionTitle>}
            <p className="text-sm leading-normal tracking-body text-secondary md:text-base">
              {body}
            </p>
          </div>
          <ButtonLink
            href={cta.href}
            variant="outline-brand"
            size="lg"
            className="self-start"
          >
            {cta.label}
          </ButtonLink>
        </div>
        <img
          loading="lazy"
          decoding="async"
          {...image}
          sizes="(min-width: 768px) min(50vw, 448px), 100vw"
          alt={imageAlt}
          className={cn(
            "aspect-[440/505] w-full max-w-md rounded-card object-cover",
            imageSide === "right" && "md:justify-self-end",
            cropImageOnMobile &&
              "aspect-auto h-96 md:aspect-[440/505] md:h-auto",
          )}
        />
      </div>
    </section>
  );
}
